/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef, useState } from 'react';
import AbilityPopup from './AbilityPopup';
import AbilitySelector from './AbilitySelector';
import Abilities from './model/Abilities';
import MarkdownText from './MarkdownText';
import Monster from './model/Monster';
import SpellcastingSelector from './SpellcastingSelector';
import SpellService from './services/SpellService';
import Section from './model/Section';
import NpcData from './model/NpcData';
import { ClassDetails } from './model/ClassDetails';

interface AbilityBlockProps {
  obj: Ability;
  mon: Monster | null;
  npcData: NpcData | null;
  blockType: string;
  sectionName: string;
  section: Section | Ability;
  sectionOptions: string[];
  onSave: (newObj: Ability) => void;
  onNpcUpdate: (npcData: NpcData) => void;
  onDelete: () => void;
  onMoveUp: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMoveDown: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSectionChange: (newSectionName: string, section: Section | Ability, oldSectionName: string) => void;
  isFirst: boolean;
  isLast: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  searchOpen: boolean;
  setSearchOpen: (searchOpen: boolean) => void;
  spellbookOpen: boolean;
  setSpellbookOpen: (searchOpen: boolean) => void;
  onRefreshTour: (tourState: string) => void;
}

const AbilityBlock: React.FC<AbilityBlockProps> = ({
  obj,
  mon,
  npcData,
  blockType,
  sectionName,
  sectionOptions,
  onSave,
  onNpcUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onSectionChange,
  isFirst,
  isLast,
  isOpen,
  setIsOpen,
  searchOpen, setSearchOpen,
  spellbookOpen, setSpellbookOpen,
  onRefreshTour
}) => {
  const abilityBlockRef = useRef<HTMLSpanElement>(null);
  const valueTextRef    = useRef<HTMLDivElement>(null);
  const [name,      setName]      = useState(obj.name);
  const [desc,      setDesc]      = useState(obj.desc);
  const [openLevel, setOpenLevel] = useState<number>(0);

  useEffect(() => {
    setName(obj.name);
    setDesc(obj.desc);
  }, [obj]);

  useEffect(() => {
    if (!isOpen) {
      setSearchOpen(false);
    }
  }, [isOpen]);

  const handleClickOutside = (event: MouseEvent) => {
    if (!valueTextRef.current) {
      return;
    }
  
    const target = event.target as HTMLElement;
    const clickedInsideAbilityBlock = abilityBlockRef.current?.contains(target) ?? false;
  
    if (
      target?.getAttribute('role') === 'option' &&
      target?.id.startsWith('react-select-')
    ) {
      // This is a click inside the select box of the search control, ignore this click
      return;
    }

    // Check if the click is inside the AbilityBlock
    if (clickedInsideAbilityBlock) {
      if (spellbookOpen && ((openLevel ?? -1) > 0)) {
        if (target.closest('.spell-selector')) {
          return;
        }
      } else if (spellbookOpen) {
        if (target.closest('.spellcasting-selector')) {
          return;
        }
      } else if (searchOpen) {
        if (target.closest('.ability-selector')) {
          // Clicked inside the currently open child; do nothing
          return;
        }
      }
    }

    // Close one level of containers, or the AbilityBlock if nothing is open:
    if (searchOpen) {
      setSearchOpen(false);
      return;
    }
    if (spellbookOpen) {
      if ((openLevel ?? -1) > 0) {
        setOpenLevel(-1);
        onRefreshTour('Spellbook level closed');
        return;
      }
      setSpellbookOpen(false);
      onRefreshTour('Spellbook closed');
      return;
    }

    if (clickedInsideAbilityBlock) {
      // The clicked inside the ability block, but not in the content, such as on the abillity name, or the whitespace after it.
      return;
    }

    // Clicked outside the AbilityBlock; close it
    setIsOpen(false);
  };

  const handleDelete = () => {
    setSearchOpen(false);
    setIsOpen(false);
    onDelete();
  };

  const handleSearch = () => {
    setSearchOpen(true);
  };
  const handleSpellbook = () => {
    setSpellbookOpen(true);
  };

  const handleSpellbookSelect = (classDetails1: ClassDetails | null, classDetails2: ClassDetails | null,
                                 spellSlots: number[], warlockSpellSlots: number[] | null) => {
    if (mon) {
      const newNpcData: NpcData = npcData ? structuredClone(npcData) : new NpcData();
      if (classDetails1?.classInfo?.name) newNpcData.primaryClassName   = classDetails1?.classInfo?.name || '';
      if (classDetails2?.classInfo?.name) newNpcData.secondaryClassName = classDetails2?.classInfo?.name || '';
      if (classDetails1?.classLevel)      newNpcData.primaryLevel       = classDetails1?.classLevel || 0;
      if (classDetails1?.classLevel)      newNpcData.secondaryLevel     = classDetails2?.classLevel || 0;
      newNpcData.spellbookSpellsArrayBySpellLevelPrimary   = classDetails1?.spellbookSpellsArrayBySpellLevel || [];
      newNpcData.spellbookSpellsArrayBySpellLevelSecondary = classDetails2?.spellbookSpellsArrayBySpellLevel || [];
      newNpcData.spellsPreparedArrayBySpellLevelPrimary    = classDetails1?.spellsPreparedArrayBySpellLevel || [];
      newNpcData.spellsPreparedArrayBySpellLevelSecondary  = classDetails2?.spellsPreparedArrayBySpellLevel || [];
      newNpcData.spellbookSpellSlugsBySpellLevelPrimary    = NpcData.getSlugsFromSpells(newNpcData.spellbookSpellsArrayBySpellLevelPrimary);
      newNpcData.spellbookSpellSlugsBySpellLevelSecondary  = NpcData.getSlugsFromSpells(newNpcData.spellbookSpellsArrayBySpellLevelSecondary);
      newNpcData.spellSlugsPreparedBySpellLevelPrimary     = NpcData.getSlugsFromSpells(newNpcData.spellsPreparedArrayBySpellLevelPrimary);
      newNpcData.spellSlugsPreparedBySpellLevelSecondary   = NpcData.getSlugsFromSpells(newNpcData.spellsPreparedArrayBySpellLevelSecondary);
      onNpcUpdate(newNpcData);
    }
    const sections: Section[] = [];
    SpellService.addSpells(sections, classDetails1, classDetails2, spellSlots, warlockSpellSlots);
    
    setSpellbookOpen(false);
    const section = sections[0];
    if (sections.length > 1) {
      section.desc += '\n\n\n\n**' + sections[1].name + '**\n' + sections[1].desc;
    }
    onSave(section);
  };

  return (
    <span className="multi-select-container" ref={abilityBlockRef}>
      {isOpen && (
        <AbilityPopup
          name={name}
          desc={desc}
          sectionName={sectionName}
          section={obj}
          sectionOptions={sectionOptions}
          showSearch={Abilities.getOptions(blockType).length > 0}
          showSpells={blockType === 'spells'}
          onChange={(newName, newDesc) => onSave({ ...obj, name: newName, desc: newDesc })}
          onDelete={handleDelete}
          onSearch={handleSearch}
          onSpellbook={handleSpellbook}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onSectionChange={onSectionChange}
          isFirst={isFirst}
          isLast={isLast}
          onClickOutside={handleClickOutside}
          hasOpenSubDialog={searchOpen || spellbookOpen}
        />
      )}
      <section
        className={`action-section edit-field${isOpen ? ' open' : ''}`}
        onMouseDown={(event) => {
          const target = event.target as HTMLElement;
          if (spellbookOpen && (openLevel ?? -1) > 0 && !target.closest('.spell-selector')) {
            setOpenLevel(-1);
            event.stopPropagation();
            return;
          }
          if (spellbookOpen && !target.closest('.spellcasting-selector')) {
            setSpellbookOpen(false);
            event.stopPropagation();
            return;
          }
          if (searchOpen && !target.closest('.ability-selector')) {
            setSearchOpen(false);
            event.stopPropagation();
            return;
          }
          setIsOpen(searchOpen || spellbookOpen || !isOpen);
          event.stopPropagation();
        }}
        ref={valueTextRef}
      >
        {obj.name ? <p className="action-head">{obj.name}.</p> : ''}
        <MarkdownText text={obj.desc} mon={mon} />
        <AbilitySelector
          isOpen={searchOpen && isOpen}
          blockType={blockType}
          onSelect={(selection) => {
            onSave({ ...obj, name: selection.name, desc: selection.desc });
            setName(selection.name);
            setDesc(selection.desc);
            setSearchOpen(false);
          }}
          onCancel={() => {
            setSearchOpen(false);
          }}
        />
        {mon && blockType === 'spells' && (
          <SpellcastingSelector isOpen={isOpen && spellbookOpen}
                                attributes={mon.attributes} 
                                npcData={npcData}
                                ability={obj}
                                onNpcUpdate={onNpcUpdate}
                                onSelect={handleSpellbookSelect}
                                onCancel={() => {setSpellbookOpen(false)}}
                                onRefreshTour={onRefreshTour}
                                onClickOutside={() => {/*handleClickOutside()*/}}
                                openLevel={openLevel} setOpenLevel={setOpenLevel} />
                                )}
      </section>
    </span>
  );
};

export default AbilityBlock;
